import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, MapPin, Users, Repeat, Bell, Trash2, Pencil, Upload, Image as ImageIcon, Mic, Loader2 } from 'lucide-react';
import { TodoItem, createTodo, deleteTodo, updateTodo } from '../lib/todos';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { createEventShareById, createEventShareFromPayload } from '../lib/shares';
import { buildICS, googleCalendarLink } from '../utils/ics';
import RoundedQRCode from './RoundedQRCode';
import { downloadQrPng, shareQrPng } from '../utils/qr';
import { addContactImage, uploadContactImage, listContactImages } from '../lib/images';
import { listContactsCore, getContactCore } from '../lib/contactsCore';
import { getClaimedContactId } from '../lib/profiles';
import { uploadAudioToStorage, firefliesUploadFromStorage, firefliesGetStatus, waitForTranscriptId, type DiarizedSegment } from '../lib/transcribe';
import { createEvent as createContactEvent } from '../lib/events';
import Dropzone from './Dropzone';
import SpeakerAssign from './SpeakerAssign';

interface EventDetailsModalProps {
    event: TodoItem | null; // null for new event
    isOpen: boolean;
    onClose: () => void;
    onSave: (event: TodoItem) => void;
    onDelete?: (id: string) => void;
    user: User;
    defaultDate?: string; // ISO string for new events
    defaultTime?: string; // HH:MM format
    // When true and an existing event is provided, open in read-only view mode first
    initialReadOnly?: boolean;
    groupId?: string | null; // ensure created events are scoped to the active group
    // When true, render inline in document flow instead of a fixed-position modal overlay
    renderInline?: boolean;
}

interface Contact {
    id: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    company?: string;
}

const RECURRENCE_OPTIONS = [
    { value: '', label: 'Does not repeat' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' },
    { value: 'yearly', label: 'Yearly' }
];

const REMINDER_OPTIONS = [
    { value: '', label: 'No reminder' },
    { value: '5', label: '5 minutes before' },
    { value: '15', label: '15 minutes before' },
    { value: '30', label: '30 minutes before' },
    { value: '60', label: '1 hour before' },
    { value: '1440', label: '1 day before' }
];

function formatDateTime(isoString: string): { date: string; time: string; isAllDay: boolean } {
    const date = new Date(isoString);
    const isAllDay = isoString.endsWith('T00:00:00.000Z');

    return {
        date: date.toISOString().split('T')[0], // YYYY-MM-DD
        time: isAllDay ? '' : date.toTimeString().slice(0, 5), // HH:MM
        isAllDay
    };
}

function createISOString(date: string, time: string, isAllDay: boolean): string {
    if (isAllDay) {
        return `${date}T00:00:00.000Z`;
    }

    const [hours, minutes] = time.split(':').map(Number);
    const dateObj = new Date(`${date}T00:00:00.000Z`);
    dateObj.setUTCHours(hours, minutes, 0, 0);
    return dateObj.toISOString();
}

// Safe variant for UI helpers that might run before defaults settle
function safeCreateISOString(date: string, time: string, isAllDay: boolean): string | null {
    try {
        if (!date || !date.trim()) return null;
        if (isAllDay) return `${date}T00:00:00.000Z`;
        if (!time || !/^\d{2}:\d{2}$/.test(time)) return null;
        return createISOString(date, time, isAllDay);
    } catch {
        return null;
    }
}

export default function EventDetailsModal({
    event,
    isOpen,
    onClose,
    onSave,
    onDelete,
    user,
    defaultDate,
    defaultTime,
    initialReadOnly,
    groupId,
    renderInline
}: EventDetailsModalProps) {
    const isEditing = !!event;

    // Form state
    const [title, setTitle] = useState('');
    const [showValidation, setShowValidation] = useState(false);
    const [description, setDescription] = useState('');
    const [location, setLocation] = useState('');
    const [date, setDate] = useState('');
    const [time, setTime] = useState('');
    const [isAllDay, setIsAllDay] = useState(false);
    const [recurrence, setRecurrence] = useState('');
    const [reminder, setReminder] = useState('');
    const [contactId, setContactId] = useState<string | null>(null);
    const [contactData, setContactData] = useState<Contact | null>(null);

    // Contact search
    const [contactQuery, setContactQuery] = useState('');
    const [contactOptions, setContactOptions] = useState<Contact[]>([]);
    const [contactFocused, setContactFocused] = useState(false);

    // Loading states
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [shareToken, setShareToken] = useState<string | null>(null);
    const [sharing, setSharing] = useState(false);
    const [qrFullscreen, setQrFullscreen] = useState(false);

    // Read-only view mode: open existing events as view-only if requested
    const [readOnly, setReadOnly] = useState<boolean>(!!(initialReadOnly && isEditing));

    // Photo attach state (for contact gallery linkage)
    const [existingPhotoUrl, setExistingPhotoUrl] = useState<string | null>(null);
    const [existingImageId, setExistingImageId] = useState<string | null>(null);
    const [photoFiles, setPhotoFiles] = useState<File[]>([]);
    const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
    const [photoUploading, setPhotoUploading] = useState<boolean>(false);
    const fileRef = useRef<HTMLInputElement | null>(null);
    const scrollRef = useRef<HTMLDivElement | null>(null);

    // Optional: Transcription flow (create a linked contact event with chat transcript)
    const [transcribeMode, setTranscribeMode] = useState(false);
    const [transFile, setTransFile] = useState<File | null>(null);
    const [transUploading, setTransUploading] = useState(false);
    const [transStatus, setTransStatus] = useState<'idle' | 'uploading' | 'processing' | 'done' | 'error'>('idle');
    const [transError, setTransError] = useState<string | null>(null);
    const [transUploadedPath, setTransUploadedPath] = useState<string | null>(null);
    const [transSegments, setTransSegments] = useState<DiarizedSegment[]>([]);
    const [transText, setTransText] = useState('');
    const [speakerLabelsMap, setSpeakerLabelsMap] = useState<string[]>([]);
    const [speakerNameMap, setSpeakerNameMap] = useState<Record<string, string>>({});
    const transFileRef = useRef<HTMLInputElement | null>(null);

    // Shared form content used by both modal and inline variants
    const renderForm = () => (
        <>
            <div className="text-xs uppercase tracking-wide text-gray-400">Details</div>
            {/* Title */}
            <div>
                <input
                    type="text"
                    placeholder="Event title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => setShowValidation(true)}
                    className={`w-full px-3 py-2 bg-gray-800 rounded text-gray-200 placeholder-gray-400 focus:outline-none ${readOnly ? 'opacity-90 cursor-default' : ''} ${showValidation && !title.trim() ? 'border border-red-500 focus:border-red-500' : 'border border-gray-700 focus:border-blue-500'
                        }`}
                    readOnly={readOnly}
                    autoFocus={!readOnly}
                />
                {showValidation && !title.trim() && <div className="mt-1 text-xs text-red-400">Title is required.</div>}
            </div>

            {/* Date & Time */}
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 focus:border-blue-500 focus:outline-none"
                        disabled={readOnly}
                    />
                    {!isAllDay && (
                        <input
                            type="time"
                            value={time}
                            onChange={(e) => setTime(e.target.value)}
                            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 focus:border-blue-500 focus:outline-none"
                            disabled={readOnly}
                        />
                    )}
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input type="checkbox" checked={isAllDay} onChange={(e) => setIsAllDay(e.target.checked)} className="rounded" disabled={readOnly} />
                    All day
                </label>
            </div>

            <div className="pt-2 text-xs uppercase tracking-wide text-gray-400">Notes & media</div>
            {/* Description */}
            <div>
                <textarea
                    placeholder="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 placeholder-gray-400 focus:border-blue-500 focus:outline-none resize-none"
                    readOnly={readOnly}
                />
            </div>

            {/* Photo (attach to contact gallery) */}
            <div className="bg-gray-900/40 border border-gray-800 rounded p-3">
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-gray-200">
                        <ImageIcon className="w-4 h-4 text-gray-400" />
                        <span className="text-sm font-medium">Photo</span>
                    </div>
                    {!readOnly && (
                        <div className="flex items-center gap-2">
                            <input
                                ref={fileRef}
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={(e) => {
                                    const files = Array.from(e.target.files || []);
                                    if (files.length) {
                                        setPhotoFiles((prev) => [...prev, ...files]);
                                        try {
                                            setPhotoPreviews((prev) => [...prev, ...files.map((f) => URL.createObjectURL(f))]);
                                        } catch { }
                                    }
                                }}
                            />
                            <button type="button" onClick={() => fileRef.current?.click()} className="px-3 py-1.5 rounded border border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700 text-xs" disabled={photoUploading}>
                                <Upload className="w-3.5 h-3.5 inline mr-1" /> {photoPreviews.length || existingPhotoUrl ? 'Add More' : 'Add'} Photos
                            </button>
                            {photoPreviews.length > 0 && (
                                <button type="button" className="px-3 py-1.5 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 text-xs" onClick={() => { setPhotoFiles([]); setPhotoPreviews([]); }}>
                                    Clear All
                                </button>
                            )}
                        </div>
                    )}
                </div>
                {photoPreviews.length > 0 || existingPhotoUrl ? (
                    <div className="space-y-2">
                        {existingPhotoUrl && photoPreviews.length === 0 && <img src={existingPhotoUrl} alt="event existing" className="w-full max-h-56 object-contain bg-black rounded border border-gray-800" />}
                        {photoPreviews.length > 0 && (
                            <div className="grid grid-cols-3 gap-2">
                                {photoPreviews.map((src, i) => (
                                    <div key={i} className="relative">
                                        <img src={src} alt={`event ${i}`} className="w-full h-24 object-cover bg-black rounded border border-gray-800" />
                                    </div>
                                ))}
                            </div>
                        )}
                        {!readOnly && !contactId && <div className="text-xs text-yellow-300">Select a contact above to save these photos to their gallery.</div>}
                    </div>
                ) : (
                    <div className="text-xs text-gray-400">{readOnly ? 'No photo attached.' : 'Optional: attach a photo; it will be saved to the contact’s gallery.'}</div>
                )}
            </div>

            <div className="pt-2 text-xs uppercase tracking-wide text-gray-400">People & place</div>
            {/* Location */}
            <div className="relative">
                <MapPin className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <input
                    type="text"
                    placeholder="Location"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                    readOnly={readOnly}
                />
            </div>

            {/* Contact */}
            <div className="relative">
                <Users className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                {selectedContact && contactId && !readOnly ? (
                    <div className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 flex items-center justify-between">
                        <div className="truncate">
                            {`${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim() || selectedContact.email || 'Contact'}
                            {selectedContact.email && <span className="text-xs text-gray-400"> · {selectedContact.email}</span>}
                        </div>
                        <button type="button" className="text-gray-400 hover:text-gray-200 text-xs" onClick={() => { setContactId(null); setContactData(null); }}>
                            Clear
                        </button>
                    </div>
                ) : (
                    <input
                        type="text"
                        placeholder="Add contact"
                        value={selectedContact ? `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim() : contactQuery}
                        onChange={(e) => {
                            setContactQuery(e.target.value);
                            setContactId(null);
                            setContactData(null);
                        }}
                        onFocus={() => !readOnly && setContactFocused(true)}
                        onBlur={() => setTimeout(() => setContactFocused(false), 200)}
                        className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 placeholder-gray-400 focus:border-blue-500 focus:outline-none"
                        readOnly={readOnly}
                    />
                )}

                {contactFocused && !readOnly && contactOptions.length > 0 && !contactId && (
                    <div className="absolute z-10 mt-1 w-full bg-gray-800 border border-gray-700 rounded shadow-xl">
                        {contactOptions.map((contact) => (
                            <button
                                key={contact.id}
                                type="button"
                                onClick={() => {
                                    setContactId(contact.id);
                                    setContactQuery('');
                                    setContactFocused(false);
                                }}
                                className="w-full text-left px-3 py-2 hover:bg-gray-700 text-gray-200 text-sm"
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <div>{`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || contact.email || contact.company || 'Unknown'}</div>
                                    {contact.company && <div className="text-[10px] text-gray-500">{contact.company}</div>}
                                </div>
                                {contact.email && <div className="text-xs text-gray-400">{contact.email}</div>}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="pt-2 text-xs uppercase tracking-wide text-gray-400">Automation</div>
            {/* Recurrence */}
            <div className="relative">
                <Repeat className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <select
                    value={recurrence}
                    onChange={(e) => setRecurrence(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 focus:border-blue-500 focus:outline-none"
                    disabled={readOnly}
                >
                    {RECURRENCE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Reminder */}
            <div className="relative">
                <Bell className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                <select
                    value={reminder}
                    onChange={(e) => setReminder(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 bg-gray-800 border border-gray-700 rounded text-gray-200 focus:border-blue-500 focus:outline-none"
                    disabled={readOnly}
                >
                    {REMINDER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            {/* Share panel (inline, appears after creating a token) */}
            {!readOnly && shareToken && (
                <div className="p-4 border border-gray-800 bg-gray-900 rounded">
                    <div className="text-sm text-gray-300 mb-2">Share this event (expires in 7 days):</div>
                    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                        {(() => {
                            const dueISO = safeCreateISOString(date, time, isAllDay);
                            const url = dueISO ? googleCalendarLink({ title: title || 'Event', description, location, startISO: dueISO, allDay: isAllDay }) : '';
                            return (
                                <a
                                    href={url || undefined}
                                    target={url ? '_blank' : undefined}
                                    rel={url ? 'noreferrer' : undefined}
                                    className={`px-3 py-2 rounded border text-sm text-center ${url ? 'bg-green-600 border-green-500 text-white' : 'bg-gray-800 border-gray-700 text-gray-500 cursor-not-allowed'}`}
                                    aria-disabled={!url}
                                >
                                    Google Calendar
                                </a>
                            );
                        })()}
                        <button onClick={downloadIcs} className="px-3 py-2 rounded bg-blue-600 border border-blue-500 text-white text-sm">
                            Download .ics
                        </button>
                        <button onClick={shareIcs} className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm">
                            Share .ics
                        </button>
                        {(() => {
                            const link = `${window.location.origin}/event/v/${shareToken}`;
                            return (
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => navigator.clipboard.writeText(link).catch(() => { })}
                                        className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm"
                                    >
                                        Copy Link
                                    </button>
                                    <a href={link} target="_blank" rel="noreferrer" className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm">
                                        Open
                                    </a>
                                </div>
                            );
                        })()}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-2">Public link: {`${window.location.origin}/event/v/${shareToken}`}</div>
                    <div className="mt-3 w-full flex flex-col items-center gap-2">
                        <div className="flex items-center gap-2">
                            {(() => {
                                const link = `${window.location.origin}/event/v/${shareToken}`;
                                return (
                                    <>
                                        <button
                                            onClick={async () => {
                                                await shareQrPng(link, `QR for ${title || 'Event'}`, link);
                                            }}
                                            className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm"
                                        >
                                            Share QR
                                        </button>
                                        <button
                                            onClick={async () => {
                                                await downloadQrPng(link, `${(title || 'Event').replace(/[^a-zA-Z0-9]/g, '_')}_QR.png`);
                                            }}
                                            className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm"
                                        >
                                            Download QR
                                        </button>
                                    </>
                                );
                            })()}
                        </div>
                        <button type="button" onClick={() => setQrFullscreen(true)} className="p-2 rounded bg-black/40 border border-gray-800 hover:bg-black/60">
                            <RoundedQRCode value={`${window.location.origin}/event/v/${shareToken}`} size={128} background="#111827" foreground="#e5e7eb" />
                            <div className="mt-1 text-[10px] text-gray-500">Tap to enlarge</div>
                        </button>
                    </div>
                </div>
            )}
        </>
    );

    // Initialize form when event changes
    useEffect(() => {
        if (event) {
            setTitle(event.title || '');
            setShowValidation(false);
            setDescription(event.description || '');
            setLocation(event.location || '');

            if (event.due_date) {
                const { date: eventDate, time: eventTime, isAllDay: eventIsAllDay } = formatDateTime(event.due_date);
                setDate(eventDate);
                setTime(eventTime);
                setIsAllDay(eventIsAllDay);
            }

            setRecurrence(event.recurrence || '');
            setReminder(event.reminder_minutes?.toString() || '');
            setContactId(event.contact_id || null);
            setContactData(null);
            // Reset view mode for existing event when switching events
            setReadOnly(!!initialReadOnly);
            // Load any existing photo attached to this calendar event (from contact_images via core)
            (async () => {
                try {
                    if (!event.id) {
                        setExistingPhotoUrl(null);
                        setExistingImageId(null);
                        return;
                    }
                    // We need a contact_id to scope the images; prefer the event's contact_id
                    const targetContactId = event.contact_id || null;
                    if (!targetContactId) { setExistingPhotoUrl(null); setExistingImageId(null); return; }
                    const imgs = await listContactImages(targetContactId);
                    const match = (imgs || []).filter(i => i.source_type === 'todo' && i.source_id === event.id).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0];
                    setExistingPhotoUrl(match?.url || null);
                    setExistingImageId(match?.id || null);
                } catch {
                    setExistingPhotoUrl(null);
                    setExistingImageId(null);
                }
            })();
            setPhotoFiles([]);
            setPhotoPreviews([]);
            // Reset transcription state when switching events
            setTranscribeMode(false);
            setTransFile(null);
            setTransUploading(false);
            setTransStatus('idle');
            setTransError(null);
            setTransUploadedPath(null);
            setTransSegments([]);
            setTransText('');
            setSpeakerLabelsMap([]);
            setSpeakerNameMap({});
        } else {
            // New event
            setTitle('');
            setShowValidation(false);
            setDescription('');
            setLocation('');
            setDate(defaultDate ? defaultDate.split('T')[0] : new Date().toISOString().split('T')[0]);
            setTime(defaultTime || '09:00');
            setIsAllDay(!defaultTime);
            setRecurrence('');
            setReminder('15');
            setContactId(null);
            setContactData(null);
            setReadOnly(false); // creating is always editable
            setExistingPhotoUrl(null);
            setExistingImageId(null);
            setPhotoFiles([]);
            setPhotoPreviews([]);
            // Reset transcription state for new event
            setTranscribeMode(false);
            setTransFile(null);
            setTransUploading(false);
            setTransStatus('idle');
            setTransError(null);
            setTransUploadedPath(null);
            setTransSegments([]);
            setTransText('');
            setSpeakerLabelsMap([]);
            setSpeakerNameMap({});
        }
    }, [event, defaultDate, defaultTime, initialReadOnly]);

    // Contact search via core
    useEffect(() => {
        if (!contactQuery.trim()) {
            setContactOptions([]);
            return;
        }

        const timeoutId = setTimeout(async () => {
            try {
                const rows = await listContactsCore(supabase as any, { query: contactQuery, limit: 8, includeArchived: false });
                const mapped = (rows as any[]).map(r => ({ id: r.id, first_name: r.first_name, last_name: r.last_name, email: r.email, company: (r as any).company || undefined }));
                setContactOptions(mapped as any);
            } catch (error) {
                setContactOptions([]);
            }
        }, 200);

        return () => clearTimeout(timeoutId);
    }, [contactQuery]);

    const selectedContact = useMemo(() => {
        return contactOptions.find(c => c.id === contactId) || contactData || null;
    }, [contactOptions, contactId, contactData]);

    // If we have a contactId but no matching option, load it for display via core
    useEffect(() => {
        (async () => {
            if (!contactId) {
                setContactData(null);
                return;
            }
            if (contactOptions.some(c => c.id === contactId)) return;
            try {
                const c = await getContactCore(supabase as any, contactId);
                setContactData((c as any) || null);
            } catch {
                setContactData(null);
            }
        })();
    }, [contactId, contactOptions]);

    const handleSave = async () => {
        if (!title.trim()) {
            setShowValidation(true);
            return;
        }

        setSaving(true);
        try {
            const dueDate = createISOString(date, time, isAllDay);

            const eventData: any = {
                title: title.trim(),
                description: description.trim() || null,
                location: location.trim() || null,
                due_date: dueDate,
                contact_id: contactId,
                recurrence: recurrence || null,
                reminder_minutes: reminder ? parseInt(reminder) : null,
                done: false
            };

            let savedEvent: TodoItem;

            // If transcription is ready, create a linked contact event carrying the chat transcript
            if (transcribeMode && transStatus === 'done') {
                if (!contactId) {
                    throw new Error('Select a contact to attach the transcribed conversation.');
                }
                // Build chat payload
                const primaryName = (selectedContact ? `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim() : '') || null;
                const displayNameFor = (lbl: string, idx: number) => speakerNameMap[lbl] || (speakerLabelsMap.length > 1 ? `Speaker ${idx + 1}` : lbl || `Speaker ${idx + 1}`);
                const segments = (transSegments || []).map((s, idx) => ({ name: displayNameFor(s.speaker || `Speaker ${idx + 1}`, idx), text: s.text || '' }));
                const participants = primaryName ? [{ name: primaryName, contactId, avatar: null }] : [];
                const chat = { primary: primaryName, participants, segments };
                const entry: any = {
                    name: 'Transcribed conversation',
                    when: dueDate ? new Date(dueDate).toISOString().slice(0, 10) : undefined,
                    where: location || undefined,
                    notes: description || undefined,
                    participants,
                    customFields: { eventType: 'Transcribed', chat, transcriptText: transText, speakerNameMap }
                };
                try {
                    const linked = await createContactEvent(contactId, entry);
                    if (linked?.id) {
                        eventData.event_id = linked.id;
                    }
                } catch (e) {
                    console.error('Failed to create linked transcribed contact event', e);
                    throw e;
                }
            }

            if (isEditing && event) {
                savedEvent = await updateTodo(event.id, eventData);
            } else {
                // Default assignment to the user's claimed contact so it shows under "Mine"
                const myContactId: string | null = await getClaimedContactId((user as any)?.id).catch(() => null);
                savedEvent = await createTodo(
                    {
                        ...eventData,
                        event_id: null,
                        assigned_contact_id: myContactId,
                        group_id: groupId || null
                    },
                    user.id
                );
            }

            // If photos were selected and we have a contact, upload and attach to that contact's gallery
            if (photoFiles.length > 0 && (contactId || savedEvent.contact_id)) {
                setPhotoUploading(true);
                try {
                    const targetContactId = (contactId || savedEvent.contact_id) as string;
                    for (const f of photoFiles) {
                        const url = await uploadContactImage(f, targetContactId);
                        // Link to this todo (calendar follow-up) so Gallery can show per-event group
                        await addContactImage(targetContactId, url, { source_type: 'todo', source_id: savedEvent.id });
                    }
                } catch (e) {
                    console.warn('Failed to attach photo to gallery', e);
                } finally {
                    setPhotoUploading(false);
                }
            }

            onSave(savedEvent);
            onClose();
        } catch (error) {
            console.error('Failed to save event:', error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!event || !onDelete) return;

        setDeleting(true);
        try {
            await deleteTodo(event.id);
            onDelete(event.id);
            onClose();
        } catch (error) {
            console.error('Failed to delete event:', error);
        } finally {
            setDeleting(false);
        }
    };
    {/* Optional: Transcribe audio and link to this item as a contact event */ }
    <div className="bg-gray-900/40 border border-gray-800 rounded p-3">
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2 text-gray-200">
                <Mic className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">Transcribe audio</span>
                {transcribeMode && (transStatus === 'done') && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-green-900/30 border border-green-700 text-green-200">Ready</span>
                )}
            </div>
            {!readOnly && (
                <button
                    type="button"
                    onClick={() => setTranscribeMode(v => !v)}
                    className="px-3 py-1.5 rounded border border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700 text-xs"
                >
                    {transcribeMode ? 'Hide' : 'Add'}
                </button>
            )}
        </div>
        {transcribeMode && !readOnly && (
            <div className="space-y-2">
                <input
                    ref={transFileRef}
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setTransFile(f);
                        setTransError(null);
                        setTransStatus('idle');
                        setTransSegments([]);
                        setTransText('');
                        setSpeakerLabelsMap([]);
                        setSpeakerNameMap({});
                    }}
                />
                <div className="space-y-3">
                    {/* Show a centered Transcribe button only after a file is chosen */}
                    {transFile && (
                        <div className="w-full flex items-center justify-center">
                            <button
                                type="button"
                                onClick={async () => {
                                    if (!transFile) return;
                                    if (!contactId) { setTransError('Select a contact first'); return; }
                                    setTransUploading(true); setTransStatus('uploading'); setTransError(null);
                                    try {
                                        const { path } = await uploadAudioToStorage(transFile, 'transcripts');
                                        setTransUploadedPath(path);
                                        setTransStatus('processing');
                                        const title = transFile.name.replace(/\.[^.]+$/, '');
                                        const attendees = selectedContact ? [{ name: `${selectedContact.first_name || ''} ${selectedContact.last_name || ''}`.trim() }] : [];
                                        const up = await firefliesUploadFromStorage({ storagePath: path, title, attendees, userId: user.id });
                                        let transcriptId: string | null = null;
                                        try { transcriptId = await waitForTranscriptId((up as any).client_reference_id); } catch { /* proceed to poll */ }
                                        const id = transcriptId || (up as any).transcript_id || (up as any).meeting_id;
                                        if (!id) throw new Error('Could not start transcription');
                                        let done = false; let tries = 0; let status: any = null;
                                        while (!done && tries < 80) {
                                            await new Promise(r => setTimeout(r, 4000));
                                            tries++;
                                            status = await firefliesGetStatus(id);
                                            done = (status?.status && /completed|done|ready/i.test(status.status)) || (status?.segments?.length > 0) || (status?.text?.length > 0);
                                        }
                                        if (!done) throw new Error('Transcription timed out');
                                        const segments: DiarizedSegment[] = status?.segments || [];
                                        const text: string = status?.text || '';
                                        setTransSegments(segments);
                                        setTransText(text);
                                        // Speaker labels and default name mapping
                                        const labels: string[] = [];
                                        const seen = new Set<string>();
                                        for (const s of (segments || [])) { const lbl = (s.speaker || '').trim() || 'Speaker'; if (!seen.has(lbl)) { seen.add(lbl); labels.push(lbl); } }
                                        const initNameMap: Record<string, string> = {};
                                        labels.forEach((lbl, i) => { initNameMap[lbl] = labels.length > 1 ? `Speaker ${i + 1}` : (lbl || `Speaker ${i + 1}`); });
                                        // Prefer showing the selected contact name as an option later
                                        setSpeakerLabelsMap(labels);
                                        setSpeakerNameMap(initNameMap);
                                        setTransStatus('done');
                                    } catch (err: any) {
                                        setTransError(err?.message || 'Failed to transcribe');
                                        setTransStatus('error');
                                    } finally {
                                        setTransUploading(false);
                                        try { if (transUploadedPath) { await (supabase as any).storage.from('audio').remove([transUploadedPath]); } } catch { /* best-effort */ }
                                        setTransUploadedPath(null);
                                    }
                                }}
                                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-md border border-blue-700 bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50"
                                disabled={!transFile || transUploading || transStatus === 'processing'}
                                title={transStatus === 'processing' ? 'Processing' : 'Transcribe'}
                                aria-label="Transcribe"
                            >
                                {transUploading || transStatus === 'processing' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mic className="w-4 h-4" />}
                                <span>{transStatus === 'processing' ? 'Processing…' : 'Transcribe'}</span>
                            </button>
                        </div>
                    )}
                    <Dropzone accept="audio/*" file={transFile} onFileSelected={setTransFile} label="Drag & drop audio here, or click to select" disabled={transUploading || transStatus === 'processing'} />
                    {transError && <div className="text-xs text-red-300">{transError}</div>}
                    {transStatus === 'done' && <div className="text-xs text-green-300">Transcript ready to attach</div>}
                </div>
                {/* Speaker mapping after transcript completes */}
                {transStatus === 'done' && speakerLabelsMap.length > 0 && (
                    <div className="mt-3 rounded-md border border-gray-800 p-3 bg-gray-900">
                        <SpeakerAssign
                            speakerLabels={speakerLabelsMap}
                            value={Object.fromEntries(speakerLabelsMap.map((lbl, i) => [lbl, { contactId: null, displayName: speakerNameMap[lbl] || `Speaker ${i + 1}` }]))}
                            onChange={(map) => {
                                const nameMap: Record<string, string> = {}
                                for (const [k, v] of Object.entries(map)) nameMap[k] = v.displayName
                                setSpeakerNameMap(nameMap)
                            }}
                        />
                        <div className="mt-2 text-[11px] text-gray-500">These names will be used in the chat-style transcript linked to this event.</div>
                    </div>
                )}
            </div>
        )}
        {transcribeMode && readOnly && (
            <div className="text-xs text-gray-400">Transcript settings hidden in read-only view.</div>
        )}
    </div>


    const handleCreateShare = async () => {
        try {
            setSharing(true);
            if (isEditing && event?.id) {
                const t = await createEventShareById(event.id, 7);
                setShareToken(t);
            } else {
                // Build a payload from current form to allow sharing before saving
                const dueISO = safeCreateISOString(date, time, isAllDay) || new Date().toISOString();
                const payload = {
                    title: title || 'Event',
                    description: description || '',
                    location: location || '',
                    due_date: dueISO,
                    all_day: isAllDay
                };
                const t = await createEventShareFromPayload(payload, 7);
                setShareToken(t);
            }
        } finally {
            setSharing(false);
        }
    };

    const downloadIcs = () => {
        const dueISO = safeCreateISOString(date, time, isAllDay) || new Date().toISOString();
        const content = buildICS({ title: title || 'Event', description, location, startISO: dueISO, allDay: isAllDay });
        const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const safe = String(title || 'event').trim().replace(/\s+/g, '_') || 'event';
        a.download = `${safe}.ics`;
        a.click();
        setTimeout(() => URL.revokeObjectURL(url), 3000);
    };

    const shareIcs = async () => {
        try {
            const dueISO = safeCreateISOString(date, time, isAllDay) || new Date().toISOString();
            const content = buildICS({ title: title || 'Event', description, location, startISO: dueISO, allDay: isAllDay });
            const safeName = String(title || 'event').trim().replace(/\s+/g, '_') || 'event';
            const blob = new Blob([content], { type: 'text/calendar' });
            const file = new File([blob], `${safeName}.ics`, { type: 'text/calendar' });
            if ((navigator as any).share) {
                const canShare = (navigator as any).canShare ? (navigator as any).canShare({ files: [file] }) : true;
                if (canShare) {
                    await (navigator as any).share({ title: title || 'Event', text: `Event: ${title || 'Event'}`, files: [file] });
                    return;
                }
            }
            // Fallback: download
            downloadIcs();
        } catch {
            downloadIcs();
        }
    };

    // Keyboard shortcuts: Esc to close, Ctrl/Cmd+S to save
    useEffect(() => {
        if (!isOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (qrFullscreen) return; // don't interfere with fullscreen QR overlay
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
            }
            const isSave = (e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey);
            if (isSave) {
                e.preventDefault();
                if (!readOnly) handleSave();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, readOnly, qrFullscreen, title, date, time, isAllDay, recurrence, reminder, contactId]);

    // Respect visibility from parent for both modal and inline variants
    if (!isOpen) return null;

    return (
        <>
            {/* Container: modal overlay vs inline card */}
            {!renderInline ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-label={isEditing ? 'Event details' : 'Create event'}
                >
                    <div className="absolute inset-0 bg-black/60" onClick={onClose} />

                    <div className="relative w-full max-w-lg bg-gray-900 border border-gray-800 rounded-lg shadow-2xl max-h-[90vh] overflow-hidden">
                        {/* Header */}
                        <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-800 bg-gray-900/95 backdrop-blur">
                            <h2 className="text-white font-semibold text-left">{readOnly ? 'Event' : isEditing ? 'Edit Event' : 'New Event'}</h2>
                            <div className="flex items-center gap-2">
                                {isEditing && readOnly && (
                                    <button onClick={() => setReadOnly(false)} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800" title="Edit">
                                        <Pencil className="w-4 h-4" />
                                    </button>
                                )}
                                <button onClick={onClose} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800" aria-label="Close">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Form */}
                        <div ref={scrollRef} className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-120px)]">{renderForm()}</div>

                        {/* Footer */}
                        <div className="sticky bottom-0 z-10 flex items-center justify-between p-4 border-t border-gray-800 bg-gray-900/95 backdrop-blur">
                            <div className="flex items-center gap-2">
                                {isEditing && onDelete && !readOnly && (
                                    <button onClick={handleDelete} disabled={deleting} className="inline-flex items-center gap-1 px-3 py-2 rounded bg-red-600 border border-red-500 text-white text-sm hover:bg-red-500 disabled:opacity-50">
                                        <Trash2 className="w-4 h-4" />
                                        {deleting ? 'Deleting...' : 'Delete'}
                                    </button>
                                )}
                            </div>

                            <div className="flex items-center gap-2">
                                <button onClick={onClose} className="px-3 py-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm">
                                    Cancel
                                </button>
                                {!readOnly && (
                                    <>
                                        <button onClick={handleSave} disabled={!title.trim() || saving || photoUploading} className="px-3 py-2 rounded bg-blue-600 border border-blue-500 text-white text-sm hover:bg-blue-500 disabled:opacity-50">
                                            {saving || photoUploading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                                        </button>
                                        <div className="hidden sm:block w-px h-6 bg-gray-800 mx-1" />
                                        <button onClick={handleCreateShare} disabled={sharing} className="px-3 py-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm" title="Create a 7-day share link">
                                            {sharing ? 'Sharing…' : 'Share'}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                // Inline card variant
                <div className="w-full bg-gray-900 border border-gray-800 rounded-lg shadow-inner overflow-hidden">
                    <div className="flex items-center justify-between p-3 border-b border-gray-800 bg-gray-900">
                        <h2 className="text-white font-semibold text-left">{readOnly ? 'Event' : isEditing ? 'Edit Event' : 'New Event'}</h2>
                        <div className="flex items-center gap-2">
                            {isEditing && readOnly && (
                                <button onClick={() => setReadOnly(false)} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800" title="Edit">
                                    <Pencil className="w-4 h-4" />
                                </button>
                            )}
                            <button onClick={onClose} className="p-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800" aria-label="Close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div ref={scrollRef} className="p-3 space-y-4">{renderForm()}</div>

                    {/* Footer (inline) */}
                    <div className="flex items-center justify-between p-3 border-t border-gray-800 bg-gray-900">
                        <div className="flex items-center gap-2">
                            {isEditing && onDelete && !readOnly && (
                                <button onClick={handleDelete} disabled={deleting} className="inline-flex items-center gap-1 px-3 py-2 rounded bg-red-600 border border-red-500 text-white text-sm hover:bg-red-500 disabled:opacity-50">
                                    <Trash2 className="w-4 h-4" />
                                    {deleting ? 'Deleting...' : 'Delete'}
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <button onClick={onClose} className="px-3 py-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm">
                                Close
                            </button>
                            {!readOnly && (
                                <>
                                    <button onClick={handleSave} disabled={!title.trim() || saving || photoUploading} className="px-3 py-2 rounded bg-blue-600 border border-blue-500 text-white text-sm hover:bg-blue-500 disabled:opacity-50">
                                        {saving || photoUploading ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                                    </button>
                                    <div className="hidden sm:block w-px h-6 bg-gray-800 mx-1" />
                                    <button onClick={handleCreateShare} disabled={sharing} className="px-3 py-2 rounded border border-gray-700 text-gray-300 hover:bg-gray-800 text-sm" title="Create a 7-day share link">
                                        {sharing ? 'Sharing…' : 'Share'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {!readOnly && qrFullscreen && shareToken && (
                <div className="fixed inset-0 z-[200] bg-black/95 flex items-center justify-center" onClick={() => setQrFullscreen(false)}>
                    <div className="flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
                        <div className="p-3 bg-black border border-gray-800">
                            <RoundedQRCode value={`${window.location.origin}/event/v/${shareToken}`} size={360} background="#000000" foreground="#FFFFFF" />
                        </div>
                        <div className="mt-4 flex items-center gap-3">
                            <button onClick={() => setQrFullscreen(false)} className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm">Close</button>
                            <button
                                onClick={async () => {
                                    const url = `${window.location.origin}/event/v/${shareToken}`;
                                    await shareQrPng(url, `QR for ${title || 'Event'}`, url);
                                }}
                                className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm"
                            >
                                Share Image
                            </button>
                            <button
                                onClick={async () => {
                                    const url = `${window.location.origin}/event/v/${shareToken}`;
                                    await downloadQrPng(url, `${(title || 'Event').replace(/[^a-zA-Z0-9]/g, '_')}_QR.png`);
                                }}
                                className="px-3 py-2 rounded bg-gray-800 border border-gray-700 text-gray-200 text-sm"
                            >
                                Download
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}